/**
 * UI for inserting entities.
 */

// Cutoff after which the remaining entities are replaced by an ellipsis
// to prevent the menu from getting too high and breaking the UI.
import { PropertyValues, LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { LinkedEntitiesManager } from "../manager";

import type { MatchInput } from "..";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";

type InsertEntityCallback = (
    start: number,
    end: number,
    entity: {
        id: string;
        name: string;
        entityId: string;
        type: string;
        href: string;
    }
) => void;

@customElement("c-insert-entity-menu")
class InsertEntityMenu extends LitElement {
    @state()
    query?: string;

    @state()
    selection: number = 0;

    @state()
    input: MatchInput;

    @state()
    options: Array<{
        name: string;
        label: string;
        insert: () => void;
    }> = [];

    @state()
    filteredOptions: Array<{
        name: string;
        label: string;
        insert: () => void;
    }> = [];

    entitiesManager: LinkedEntitiesManager;
    insertEntityCallback: InsertEntityCallback;

    constructor(
        input: MatchInput,
        insertEntity: InsertEntityCallback,
        entitiesManager: LinkedEntitiesManager
    ) {
        super();
        this.input = input;
        this.insertEntityCallback = insertEntity;
        this.entitiesManager = entitiesManager;

        this.options = this.entitiesManager.entities
            .map((entity) => ({
                name: entity.name,
                label: entity.name,
                insert: () =>
                    this.insertEntityCallback(
                        this.input.start,
                        this.input.end,
                        {
                            id: entity.id,
                            name: entity.name,
                            entityId: entity.id,
                            type: entity.type,
                            href: entity.href,
                        }
                    ),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
        this.filterByQuery(this.input.match?.groups["query"]);
    }

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("input")) {
            this.filterByQuery(this.input.match?.groups["query"]);
        }
    }

    down() {
        this.selection = (this.selection + 1) % this.filteredOptions.length;
    }

    up() {
        // Modulo will preserve the sign, so we add length
        // to make sure the result falls in the range [0, length - 1]
        this.selection =
            (this.selection + this.filteredOptions.length - 1) %
            this.filteredOptions.length;
    }

    insertSelection() {
        this.filteredOptions[this.selection].insert();
    }

    filterByQuery(query?: string) {
        if (this.query === query) {
            return;
        }
        this.query = query;
        if (query === undefined) {
            this.filteredOptions = this.options;
        } else {
            this.filteredOptions = this.options.filter(({ label }) =>
                label.toLowerCase().includes(this.query.toLowerCase())
            );
        }
        this.selection = 0;
    }

    render() {
        return html`
            <sl-menu>
                ${this.filteredOptions.length
                    ? this.filteredOptions.map(
                          (option, i) =>
                              html`<sl-menu-item
                                  value=${option.name}
                                  @keydown="${(e: KeyboardEvent) => {
                                      if (e.key === "ArrowDown") {
                                          e.preventDefault();
                                          this.down();
                                      } else if (e.key === "ArrowUp") {
                                          e.preventDefault();
                                          this.up();
                                      }
                                  }}"
                                  @mouseenter="${() => {
                                      this.selection = i;
                                  }}"
                                  @click=${option.insert}
                                  class=${classMap({
                                      selected: this.selection === i,
                                  })}
                              >
                                  ${option.label}
                              </sl-menu-item>`
                      )
                    : html`<div>Nothing found.</div>`}
            </sl-menu>
        `;
    }

    static styles = css`
        sl-menu-item:focus-visible::part(base),
        sl-menu-item.selected::part(base) {
            background: var(--stekz-secondary-background);
            color: var(--text-color);
        }

        sl-menu-item.selected[disabled]::part(base) {
            background-color: var(--sl-color-neutral-100);
            color: var(--text-color);
        }
    `;
}

export { InsertEntityMenu };
